import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { Dialog, DialogContent } from "./ui/dialog";

const ALLOWED_IMG_HOSTS = new Set([
  "cdn.openart.ai",
  "images.openart.ai",
  "static.prod-images.emergentagent.com",
]);

const schema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), "img"],
  attributes: {
    ...defaultSchema.attributes,
    img: ["src", "alt", "title", "loading"],
  },
  protocols: {
    ...defaultSchema.protocols,
    src: ["https"],
  },
};

function isAllowedImg(src) {
  if (!src) return false;
  try {
    const u = new URL(src);
    return u.protocol === "https:" && ALLOWED_IMG_HOSTS.has(u.hostname);
  } catch {
    return false;
  }
}

function MDImage({ src, alt, title, setLightbox }) {
  if (!isAllowedImg(src)) {
    return (
      <span className="inline-block text-[11px] font-mono text-muted-foreground border border-dashed border-border rounded px-2 py-1">
        [image blocked: {alt || "unknown host"}]
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt || ""}
      title={title || ""}
      loading="lazy"
      onClick={() => setLightbox({ src, alt })}
      className="max-w-full rounded-lg my-2 cursor-zoom-in hover:opacity-90 transition-opacity"
      data-testid="md-image"
    />
  );
}

export function Markdown({ children, className = "" }) {
  const [lightbox, setLightbox] = useState(null);
  return (
    <>
      <div className={`prose-markdown ${className}`} data-testid="markdown-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[[rehypeSanitize, schema]]}
          components={{
            a: ({ node, ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer" />
            ),
            img: ({ node, ...props }) => (
              <MDImage {...props} setLightbox={setLightbox} />
            ),
          }}
        >
          {children || ""}
        </ReactMarkdown>
      </div>
      <Dialog open={!!lightbox} onOpenChange={(v) => !v && setLightbox(null)}>
        <DialogContent className="max-w-4xl p-2 bg-black/90 border-0" data-testid="md-lightbox">
          {lightbox && (
            <img
              src={lightbox.src}
              alt={lightbox.alt || ""}
              className="w-full h-auto rounded"
              onClick={() => window.open(lightbox.src, "_blank")}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
